/* Pure cart rules. Prices and allocations always come from Shopify. */
(function (root, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else root.ZenteinCartRules = api;
})(typeof window !== 'undefined' ? window : globalThis, function () {
  const props = line => line.properties || {};
  const identity = line => JSON.stringify([
    String(line.variant_id || line.id),
    Object.entries(props(line)).sort(([a], [b]) => a.localeCompare(b)),
    String(line.selling_plan_allocation?.selling_plan?.id || line.selling_plan || '')
  ]);
  const sum = lines => lines.reduce((n, l) => n + Number(l.quantity || 0), 0);
  const total = lines => lines.reduce((n, l) => n + Number(l.final_line_price || 0), 0);
  function groups(cart, config) {
    const groups = new Map();
    for (const line of cart.items || []) {
      const id = props(line)._byb_id;
      if (!id) continue;
      if (!groups.has(id)) groups.set(id, {id, lines: [], paid: [], gifts: [], bags: [], unknown: []});
      const group = groups.get(id); group.lines.push(line);
      const role = props(line)._byb_role, v = String(line.variant_id || line.id);
      if (role === 'paid' && config.snackIds.includes(v)) group.paid.push(line);
      else if (role === 'reward' && config.giftIds.includes(v)) group.gifts.push(line);
      else if (role === 'addon' && config.addonIds.includes(v)) group.bags.push(line);
      else group.unknown.push(line);
    }
    return [...groups.values()].map(g => {
      g.count = sum(g.paid); g.earned = Math.floor(g.count / config.rewardInterval);
      g.claimed = sum(g.gifts); g.unclaimed = Math.max(0, g.earned - g.claimed);
      g.valid = g.count >= config.minimum && !g.unknown.length;
      g.total = total(g.lines); return g;
    });
  }
  function validate(cart, config) {
    const issues = [];
    if ((cart.items || []).some(l => props(l)._zc_replacing)) issues.push({repair:true,message:'A box update needs to finish. Use Finish box update to safely recover your selection.'});
    for (const g of groups(cart, config)) {
      if (!g.valid) issues.push({group: g.id, message: g.count < config.minimum
        ? 'Your custom box needs '+(config.minimum-g.count)+' more paid snacks. Choose at least '+config.minimum+' to continue.'
        : 'One custom-box item needs to be checked. Edit this box before checkout.'});
      if (g.claimed > g.earned || g.gifts.some(l => Number(l.final_line_price) !== 0))
        issues.push({group: g.id, message: 'A complimentary square is no longer free. Edit this box or remove the gift before checkout.'});
    }
    for (const l of cart.items || []) {
      if (config.snackIds.includes(String(l.variant_id || l.id)) && !props(l)._byb_id && props(l)._zlab_role !== 'gift')
        issues.push({key: l.key, message: 'Please choose these individual snacks through Build Your Box.'});
      if (props(l)._zlab_role === 'gift' && Number(l.final_line_price) !== 0)
        issues.push({key: l.key, message: 'This powder-bundle gift is no longer free. Restore both powders or remove the gift before checkout.'});
    }
    return issues;
  }
  function editPlan(cart, config, key, quantity) {
    const line = cart.items.find(l => l.key === key);
    if (!line) throw new Error('This item changed. Review the refreshed cart and try again.');
    if (!Number.isInteger(quantity) || quantity < 0) throw new Error('Enter a whole quantity of zero or more.');
    const updates = {[key]: quantity}, warnings = [];
    const g = groups(cart, config).find(g => g.id === props(line)._byb_id);
    if (g && props(line)._byb_role === 'paid') {
      const count = g.count - line.quantity + quantity;
      const earned = Math.floor(count / config.rewardInterval);
      let allowance = earned;
      for (const gift of g.gifts) {
        const keep = Math.min(gift.quantity, allowance); allowance -= keep;
        if (keep !== gift.quantity) updates[gift.key] = keep;
      }
      if (g.claimed > earned) warnings.push('This edit removes '+(g.claimed-earned)+' complimentary square(s) because the box will no longer qualify.');
      if (count < config.minimum) {
        if (g.bags.length) warnings.push('The associated bags will lose their 15% custom-box discount. Other eligible offers may still apply.');
        warnings.push(count === 0 ? 'This removes the last snack. Use Remove box to remove the complete box, or edit it to keep your choices.'
          : 'This leaves '+count+' paid snacks. You will need to repair or remove the box before checkout.');
      }
    }
    if (props(line)._zlab_bundle_id && props(line)._zlab_role === 'powder' && quantity === 0) {
      const gifts = cart.items.filter(l => props(l)._zlab_bundle_id === props(line)._zlab_bundle_id && props(l)._zlab_role === 'gift');
      gifts.forEach(l => updates[l.key] = 0);
      if (gifts.length) warnings.push('The two-powder offer will end. Its complimentary squares will be removed and the remaining powder will use its applicable price.');
    }
    return {updates, warnings, removed: quantity === 0 ? cart.items.filter(l => updates[l.key] === 0) : []};
  }
  function tier(cart, config) {
    const eligible = (cart.items || []).filter(l => config.eligibleProductIds.includes(String(l.product_id)) &&
      !['paid','reward'].includes(props(l)._byb_role) && props(l)._zlab_role !== 'gift');
    const count = sum(eligible), rate = count >= 5 ? 15 : count >= 3 ? 10 : count >= 2 ? 5 : 0;
    const titles = eligible.flatMap(l => (l.line_level_discount_allocations || []).map(a => a.discount_application?.title || ''))
      .concat((cart.cart_level_discount_applications || []).map(a => a.title || ''));
    const applied = rate && titles.some(t => config.tierTitles[rate] === t);
    if (!count) return null;
    if (count === 1) return 'Add 1 eligible item to save 5%.';
    if (!applied) return 'Your applicable savings are included below. Offers may have different eligibility.';
    if (count === 2) return '5% savings applied. Add 1 eligible item to save 10%.';
    if (count === 3) return '10% savings applied.';
    if (count === 4) return '10% savings applied. Add 1 eligible item to save 15%.';
    return '15% savings applied.';
  }
  function describe(cart, config) {
    const boxes = groups(cart, config), ordinary = (cart.items || []).filter(l => !props(l)._byb_id);
    const parts = [];
    if (boxes.length) parts.push(boxes.length+' custom box'+(boxes.length === 1 ? '' : 'es'));
    if (sum(ordinary)) parts.push(sum(ordinary)+' other item'+(sum(ordinary) === 1 ? '' : 's'));
    return boxes.length ? parts.join(' · ') : (cart.item_count || 0)+' item'+(cart.item_count === 1 ? '' : 's');
  }
  return {props, identity, sum, total, groups, validate, editPlan, tier, describe};
});
